import NoAnalysis from '@components/dynamic-survey/details/NoAnalysis';
import ErrorBoundary from '@components/ErrorBoundary';
import Spinner from '@components/spinner/Spinner';
import {
  useFetchInterviewCodebook,
  useFetchTranscriptList,
  useSingleInterview,
} from '@hooks/getQueries';
import DownloadIcon from '@mui/icons-material/Download';

import CodeQuotesModal from '@components/interview/Analysis/CodeQuotesModal';
import usePermission from '@hooks/usePermission';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import TableViewIcon from '@mui/icons-material/TableView';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { saveAs } from 'file-saver';
import { Suspense, useState } from 'react';
import { useParams } from 'react-router';
import { useGetCodeColorByCategory } from '../Hooks/useGetCodeColorByCategory';
import CodebookDetailsView from './codebook/CodebookDetailsView';
import CodebookTableView from './codebook/CodebookTableView';

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2,
  },
}));

const Codebook = () => {
  const classes = useStyles();
  const { id } = useParams();
  const [openCodeModal, setOpenCodeModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const { data: transcripts } = useFetchTranscriptList(id);
  const [dataView, setDataView] = useState('table');

  const { data: interviewCodebook, isPending } = useFetchInterviewCodebook({ studyId: id });
  const { getColorByCategory } = useGetCodeColorByCategory(id);
  const { data: interview } = useSingleInterview(id);
  const { data: permissions } = usePermission({ study: interview });

  const canEdit = permissions?.studyPermission?.edit || false;

  const handleChipClick = (code) => {
    setSelectedCode(code);
    setOpenCodeModal(true);
  };

  const [columns, setColumns] = useState([
    { key: 'category', label: 'Category Name / Description', width: 300 },
    { key: 'summary', label: 'Summary', width: 300 },
    { key: 'codes', label: 'Codes', width: 600 },
    { key: 'action', label: 'Action', width: 40 },
  ]);

  const handleDownload = () => {
    const now = new Date();
    const dateString = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
    const csvRows = [['Category', 'Saliency', 'Description', 'Summary', 'Codes', 'Example Quotes']];

    // Iterate over categories and format the data
    for (const category in interviewCodebook?.categories) {
      const categoryData = interviewCodebook?.categories[category];
      const codes = `"${categoryData.codes_included.join(', ')}"`;
      const description = `"${categoryData.description.replace(/\n/g, ' ').replace(/"/g, '""')}"`;
      const summary = `"${categoryData?.summary.replace(/\n/g, ' ').replace(/"/g, '""')}"`;
      const exampleQuotes = `"${categoryData.example_quotes.map((quote) => quote.text.replace(/"/g, '""')).join('; ')}"`;

      csvRows.push([category, categoryData.saliency, description, summary, codes, exampleQuotes]);
    }
    const csvString = csvRows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    saveAs(blob, `${id}_${dateString}_codebook.csv`);
  };

  if (isPending) return <Spinner />;

  if (!interviewCodebook || !interviewCodebook?.categories) {
    return <NoAnalysis studyId={id} isInterview={true} />;
  }

  const closeModal = () => {
    setOpenCodeModal(false);
    setSelectedCode(null);
  };

  console.log(selectedCode);

  return (
    <Box pt={1.8}>
      <Box className={classes.toolbar}>
        <Box px={2}>
          <Typography variant="h6" mb={2}>
            Codebook
          </Typography>
          {/* <Alert severity="info">
            There is new changes in codebook try to re-generate the codebook.
          </Alert> */}
          <Box display="flex" gap={2} alignItems={'center'}>
            <Box display={'flex'} height={'60px'} justifyContent={'end'}>
              <Box
                sx={{
                  width: 'min-content',

                  display: 'flex',
                  gap: '20px',
                  backgroundColor: '#7D7D7D33',
                  borderRadius: '8px',
                  padding: '5px',
                  paddingX: '8px',
                  cursor: 'pointer',
                  marginTop: '10px',
                  marginBottom: '10px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    paddingY: '5px',
                    paddingX: '10px',
                    borderRadius: '6px',
                    backgroundColor: dataView === 'table' ? 'white' : '',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => setDataView('table')}
                >
                  <TableViewIcon sx={{ marginRight: '5px' }} /> <Typography>Table View</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    paddingY: '5px',
                    paddingX: '10px',
                    borderRadius: '6px',
                    backgroundColor: dataView === 'detail' ? 'white' : '',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => setDataView('detail')}
                >
                  <DescriptionIcon sx={{ marginRight: '3px', fontSize: '24px' }} /> Detailed View
                </Box>
              </Box>
            </Box>
            <Button
              variant="custom"
              color="primary"
              sx={{ height: '40px' }}
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Box>
        </Box>
      </Box>

      {dataView == 'table' ? (
        <CodebookTableView
          columns={columns}
          setColumns={setColumns}
          interviewCodebook={interviewCodebook}
          setSelectedCode={setSelectedCode}
          setOpenCodeModal={setOpenCodeModal}
          handleChipClick={handleChipClick}
          getColorByCategory={getColorByCategory}
          openCodeModal={openCodeModal}
          id={id}
          canEdit={canEdit}
        />
      ) : (
        <CodebookDetailsView
          setSelectedCode={setSelectedCode}
          interviewCodebook={interviewCodebook}
          handleChipClick={handleChipClick}
          getColorByCategory={getColorByCategory}
        />
      )}
      {/* Modal for Code Details */}

      {openCodeModal && (
        <Dialog open={openCodeModal} onClose={closeModal} size="md" maxWidth="800px">
          <DialogTitle>
            <Typography variant="h6">Code Details</Typography>
          </DialogTitle>
          <IconButton
            onClick={closeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Divider />
          <DialogContent
            sx={{ maxWidth: '90vw', minWidth: '600px', position: 'relative', padding: '0' }}
          >
            <ErrorBoundary>
              <Suspense fallback={<Spinner />}>
                <CodeQuotesModal
                  transcripts={transcripts}
                  open={openCodeModal}
                  closeModal={closeModal}
                  studyId={id}
                  setSelectedCode={setSelectedCode}
                  openCode={selectedCode}
                  queryKey="codebook"
                  canEdit={canEdit}
                />
              </Suspense>
            </ErrorBoundary>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default Codebook;
